import React, { useState, useEffect } from 'react';
import { 
  Home, Users, Building2, FolderKanban, MessageSquare, 
  CheckSquare, Calendar, Heart, Plus, Search, Bell,
  Video, Phone, User, Settings, ChevronRight, Sparkles,
  BookOpen, Shield, Zap, X, Send, Clock, AlertCircle,
  Image as ImageIcon, ChevronDown, ChevronUp, FileText,
  Link as LinkIcon, Upload, Edit, Trash2, Eye
} from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import EpicLoreLibrary from './EpicLoreLibrary';
import SixPerspectivesPortal from './SixPerspectivesPortal';
import { streamMairyChat } from '../utils/streamingApi';

const GuildProjectManagement = ({ onNavigateBack, userToken }) => {
  const [currentView, setCurrentView] = useState('planet'); // planet, hamlet, family, project, room
  const [selectedHamlet, setSelectedHamlet] = useState(null);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [orbPhase, setOrbPhase] = useState(0);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [tasks, setTasks] = useState([]);
  const [showRemedyCouncil, setShowRemedyCouncil] = useState(false);
  const [showLoreScrapbook, setShowLoreScrapbook] = useState(false);
  const [showIconUpload, setShowIconUpload] = useState(false);
  const [iconUploadTarget, setIconUploadTarget] = useState(null);
  const [showEpicLoreLibrary, setShowEpicLoreLibrary] = useState(false);
  
  // Creation modals
  const [showCreateHamlet, setShowCreateHamlet] = useState(false);
  const [showCreateFamily, setShowCreateFamily] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showSixPerspectives, setShowSixPerspectives] = useState(false);
  
  // Enhanced Features State
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarView, setCalendarView] = useState('month'); // 'week' or 'month'
  const [showDocuments, setShowDocuments] = useState(false);
  const [taskCategory, setTaskCategory] = useState('all'); // 'all', 'work', 'personal', 'planning', 'logistics'
  const [taskPriority, setTaskPriority] = useState('all'); // 'all', 'high', 'medium', 'low'
  const [showSecretHistory, setShowSecretHistory] = useState(false);
  const [mairyThinking, setMairyThinking] = useState(false);
  const [mairyStreaming, setMairyStreaming] = useState(false);
  const [mairyStreamContent, setMairyStreamContent] = useState('');
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [documents, setDocuments] = useState([]);
  
  // Data state - using mock data initially, will connect to backend
  const [planetData, setPlanetData] = useState({
    name: "Seedy Peeps Federation",
    hamlets: []
  });

  // Orbital animation for Grant spiral - 50% slower
  useEffect(() => {
    const spiralInterval = setInterval(() => {
      setOrbPhase(prev => (prev + 0.1) % 360);
    }, 100);
    return () => clearInterval(spiralInterval);
  }, []);

  // Initialize enhanced data
  useEffect(() => {
    // Initialize calendar events
    setCalendarEvents([
      {
        id: 'e1',
        title: 'Family Reunion Planning Meeting',
        date: new Date(2025, 9, 20, 14, 0), // Oct 20, 2025 at 2pm
        duration: 120,
        category: 'planning',
        attendees: ['Craig', 'Maria', 'John']
      },
      {
        id: 'e2',
        title: 'Budget Review Session',
        date: new Date(2025, 9, 22, 10, 0),
        duration: 90,
        category: 'logistics'
      },
      {
        id: 'e3',
        title: 'Vendor Coordination Call',
        date: new Date(2025, 9, 25, 15, 30),
        duration: 60,
        category: 'work'
      }
    ]);
    
    // Initialize documents
    setDocuments([
      {
        id: 'd1',
        name: 'Family Reunion Budget 2025.xlsx',
        type: 'spreadsheet',
        url: '#',
        uploadedBy: 'Craig',
        uploadedAt: new Date(2025, 8, 15),
        size: '2.4 MB'
      },
      {
        id: 'd2',
        name: 'Venue Contract - Mountain Lodge',
        type: 'pdf',
        url: '#',
        uploadedBy: 'Maria',
        uploadedAt: new Date(2025, 8, 20),
        size: '856 KB'
      },
      {
        id: 'd3',
        name: 'Guest List & RSVP Tracker',
        type: 'link',
        url: 'https://docs.google.com',
        uploadedBy: 'John',
        uploadedAt: new Date(2025, 9, 1)
      }
    ]);
  }, []);
  
  // Initialize with mock data on first load
  useEffect(() => {
    if (planetData.hamlets.length === 0) {
      setPlanetData({
        name: "Seedy Peeps Federation",
        hamlets: [
      {
        id: 'h1',
        name: 'North Star Hamlet',
        icon: '🌟',
        families: [
          {
            id: 'f1',
            name: 'Thompson Family',
            icon: '🏡',
            members: 5,
            projects: [
              {
                id: 'p1',
                name: 'Family Reunion 2025',
                icon: '🎉',
                status: 'active',
                progress: 65,
                rooms: [
                  { id: 'r1', name: 'Planning Salon', type: 'chat', unread: 3 },
                  { id: 'r2', name: 'Budget Room', type: 'tasks', unread: 0 },
                  { id: 'r3', name: 'Guest Coordination', type: 'chat', unread: 1 }
                ]
              },
              {
                id: 'p2',
                name: 'Home Renovation',
                icon: '🏠',
                status: 'active',
                progress: 40,
                rooms: [
                  { id: 'r4', name: 'Design Ideas', type: 'chat', unread: 0 },
                  { id: 'r5', name: 'Contractor Tasks', type: 'tasks', unread: 2 }
                ]
              }
            ]
          },
          {
            id: 'f2',
            name: 'Chen Family',
            icon: '🏡',
            members: 4,
            projects: [
              {
                id: 'p3',
                name: 'Education Fund',
                icon: '📚',
                status: 'active',
                progress: 80,
                rooms: [
                  { id: 'r6', name: 'Strategy Salon', type: 'chat', unread: 0 }
                ]
              }
            ]
          }
        ]
      },
      {
        id: 'h2',
        name: 'Green Valley Hamlet',
        icon: '🌲',
        families: [
          {
            id: 'f3',
            name: 'Regenerative Agriculture Co-op',
            icon: '🌱',
            members: 12,
            projects: [
              {
                id: 'p4',
                name: 'Spring Planting',
                icon: '🌾',
                status: 'active',
                progress: 50,
                rooms: [
                  { id: 'r7', name: 'Main Salon', type: 'chat', unread: 5 },
                  { id: 'r8', name: 'Tasks & Timeline', type: 'tasks', unread: 3 }
                ]
              }
            ]
          }
        ]
      }
    ]
      });
    }
  }, []);

  // Navigation handlers
  const handleHamletClick = (hamlet) => {
    setSelectedHamlet(hamlet);
    setCurrentView('hamlet');
  };

  const handleFamilyClick = (family) => {
    setSelectedFamily(family);
    setCurrentView('family');
  };

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setCurrentView('project');
  };

  const handleRoomClick = (room) => {
    setSelectedRoom(room);
    setCurrentView('room');
    // Load room messages
    loadRoomMessages(room.id);
  };

  // Creation handlers
  const handleCreateHamlet = (hamletData) => {
    const newHamlet = {
      id: `h${Date.now()}`,
      name: hamletData.name,
      icon: hamletData.icon || '🌟',
      iconImage: hamletData.iconImage || null,
      families: []
    };
    
    // Force immediate update
    const updatedPlanetData = {
      ...planetData,
      hamlets: [...planetData.hamlets, newHamlet]
    };
    setPlanetData(updatedPlanetData);
    setShowCreateHamlet(false);
    
    // Force re-render by updating selected items if needed
    if (currentView === 'planet') {
      // Trigger re-render
      setCurrentView('planet');
    }
  };

  const handleCreateFamily = (familyData) => {
    const newFamily = {
      id: `f${Date.now()}`,
      name: familyData.name,
      icon: familyData.icon || '🏡',
      iconImage: familyData.iconImage || null,
      type: familyData.type || 'family', // 'family' or 'enterprise'
      members: parseInt(familyData.members) || 1,
      projects: []
    };
    
    const updatedPlanetData = {
      ...planetData,
      hamlets: planetData.hamlets.map(h => 
        h.id === selectedHamlet.id 
          ? { ...h, families: [...h.families, newFamily] }
          : h
      )
    };
    
    setPlanetData(updatedPlanetData);
    
    // Update selectedHamlet to reflect the new family
    const updatedHamlet = updatedPlanetData.hamlets.find(h => h.id === selectedHamlet.id);
    setSelectedHamlet(updatedHamlet);
    
    setShowCreateFamily(false);
  };

  const handleCreateProject = (projectData) => {
    const newProject = {
      id: `p${Date.now()}`,
      name: projectData.name,
      icon: projectData.icon || '📋',
      iconImage: projectData.iconImage || null,
      description: projectData.description || '',
      status: 'active',
      progress: 0,
      rooms: [],
      // Project can function as a room itself
      messages: [],
      tasks: [],
      documents: [],
      notes: [],
      links: []
    };
    
    const updatedPlanetData = {
      ...planetData,
      hamlets: planetData.hamlets.map(h => 
        h.id === selectedHamlet.id 
          ? {
              ...h,
              families: h.families.map(f =>
                f.id === selectedFamily.id
                  ? { ...f, projects: [...f.projects, newProject] }
                  : f
              )
            }
          : h
      )
    };
    
    setPlanetData(updatedPlanetData);
    
    // Update selected family to reflect the new project
    const updatedHamlet = updatedPlanetData.hamlets.find(h => h.id === selectedHamlet.id);
    const updatedFamily = updatedHamlet?.families.find(f => f.id === selectedFamily.id);
    setSelectedFamily(updatedFamily);
    
    setShowCreateProject(false);
  };

  const handleCreateRoom = (roomData) => {
    const newRoom = {
      id: `r${Date.now()}`,
      name: roomData.name,
      icon: roomData.icon || '💬',
      iconImage: roomData.iconImage || null,
      type: roomData.type,
      unread: 0,
      messages: [],
      tasks: [],
      documents: [],
      notes: [],
      links: []
    };
    
    const updatedPlanetData = {
      ...planetData,
      hamlets: planetData.hamlets.map(h => 
        h.id === selectedHamlet.id 
          ? {
              ...h,
              families: h.families.map(f =>
                f.id === selectedFamily.id
                  ? {
                      ...f,
                      projects: f.projects.map(p =>
                        p.id === selectedProject.id
                          ? { ...p, rooms: [...p.rooms, newRoom] }
                          : p
                      )
                    }
                  : f
              )
            }
          : h
      )
    };
    
    setPlanetData(updatedPlanetData);
    
    // Update selected project to reflect new room
    const updatedHamlet = updatedPlanetData.hamlets.find(h => h.id === selectedHamlet.id);
    const updatedFamily = updatedHamlet?.families.find(f => f.id === selectedFamily.id);
    const updatedProject = updatedFamily?.projects.find(p => p.id === selectedProject.id);
    setSelectedProject(updatedProject);
    
    setShowCreateRoom(false);
  };

  const handleCreateTask = (taskData) => {
    const newTask = {
      id: `t${Date.now()}`,
      text: taskData.text,
      assignedTo: taskData.assignedTo || 'Unassigned',
      dueDate: taskData.dueDate || 'No due date',
      priority: taskData.priority || 'medium',
      status: 'pending',
      context: `Created in ${selectedRoom.name}`
    };
    
    setTasks(prev => [...prev, newTask]);
    setShowCreateTask(false);
  };

  const handleToggleTask = (taskId) => {
    setTasks(prev => prev.map(task =>
      task.id === taskId
        ? { ...task, status: task.status === 'pending' ? 'completed' : 'pending' }
        : task
    ));
  };

  const handleDeleteTask = (taskId) => {
    setTasks(prev => prev.filter(task => task.id !== taskId));
  };

  const loadRoomMessages = (roomId) => {
    // Mock messages with AI-detected tasks
    setMessages([
      {
        id: '1',
        sender: 'Craig',
        text: 'Hey everyone, we need to finalize the guest list by next Friday',
        timestamp: new Date(Date.now() - 3600000),
        hasTask: true,
        taskSuggestion: 'Finalize guest list',
        taskDueDate: 'Next Friday'
      },
      {
        id: '2',
        sender: 'M.ai.ry',
        text: '✨ I detected a task in Craig\'s message. Would you like me to create it?',
        timestamp: new Date(Date.now() - 3500000),
        isAI: true
      },
      {
        id: '3',
        sender: 'You',
        text: 'Yes! Also, we should coordinate with the venue about catering options',
        timestamp: new Date(Date.now() - 3000000),
        hasTask: true
      }
    ]);

    setTasks([
      {
        id: 't1',
        text: 'Finalize guest list',
        assignedTo: 'Craig',
        dueDate: 'Next Friday',
        priority: 'high',
        status: 'pending',
        context: 'From Planning Salon discussion on Dec 1'
      },
      {
        id: 't2',
        text: 'Contact venue about catering',
        assignedTo: 'You',
        dueDate: 'This week',
        priority: 'medium',
        status: 'pending',
        context: 'From Planning Salon discussion today'
      }
    ]);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || mairyStreaming) return;
    
    const userMessage = newMessage.trim();
    const message = {
      id: Date.now().toString(),
      sender: 'You',
      text: userMessage,
      timestamp: new Date(),
      hasTask: false
    };
    
    setMessages([...messages, message]);
    setNewMessage('');
    
    // Start M.ai.ry streaming response
    setMairyStreaming(true);
    setMairyStreamContent('');
    setMairyThinking(true);
    
    try {
      await streamMairyChat(
        {
          player_id: 'test',
          message: userMessage,
          session_id: `guild_${selectedProject?.id || 'general'}_${Date.now()}`,
          story_context: {
            project_id: selectedProject?.id,
            project_name: selectedProject?.name,
            family_id: selectedFamily?.id,
            hamlet_id: selectedHamlet?.id
          }
        },
        // onChunk - accumulate streamed content
        (chunk) => {
          setMairyStreamContent(prev => prev + chunk);
          setMairyThinking(false);
        },
        // onComplete - finalize message
        (fullResponse) => {
          const aiMessage = {
            id: (Date.now() + 1).toString(),
            sender: 'M.ai.ry',
            text: fullResponse,
            timestamp: new Date(),
            isAI: true,
            streaming: true
          };
          setMessages(prev => [...prev, aiMessage]);
          setMairyStreamContent('');
          setMairyStreaming(false);
          setMairyThinking(false);
        },
        // onError
        (error) => {
          console.error('M.ai.ry streaming error:', error);
          const errorMessage = {
            id: (Date.now() + 1).toString(),
            sender: 'M.ai.ry',
            text: '⚡ Consciousness field static... Please try again.',
            timestamp: new Date(),
            isAI: true,
            error: true
          };
          setMessages(prev => [...prev, errorMessage]);
          setMairyStreamContent('');
          setMairyStreaming(false);
          setMairyThinking(false);
        }
      );
    } catch (error) {
      console.error('Send error:', error);
      setMairyStreaming(false);
      setMairyThinking(false);
      setMairyStreamContent('');
    }
  };

  // Breadcrumb navigation
  const renderBreadcrumbs = () => {
    const crumbs = [];
    
    crumbs.push(
      <button
        key="planet"
        onClick={() => setCurrentView('planet')}
        className="text-amber-600 hover:text-amber-700 transition-colors flex items-center gap-1"
      >
        🌍 Planet
      </button>
    );

    if (currentView !== 'planet') {
      crumbs.push(<ChevronRight key="sep1" className="text-stone-400" size={16} />);
      crumbs.push(
        <button
          key="hamlet"
          onClick={() => setCurrentView('hamlet')}
          className="text-amber-600 hover:text-amber-700 transition-colors"
        >
          {selectedHamlet?.icon} {selectedHamlet?.name}
        </button>
      );
    }

    if (currentView === 'family' || currentView === 'project' || currentView === 'room') {
      crumbs.push(<ChevronRight key="sep2" className="text-stone-400" size={16} />);
      crumbs.push(
        <button
          key="family"
          onClick={() => setCurrentView('family')}
          className="text-amber-600 hover:text-amber-700 transition-colors"
        >
          {selectedFamily?.icon} {selectedFamily?.name}
        </button>
      );
    }

    if (currentView === 'project' || currentView === 'room') {
      crumbs.push(<ChevronRight key="sep3" className="text-stone-400" size={16} />);
      crumbs.push(
        <button
          key="project"
          onClick={() => setCurrentView('project')}
          className="text-amber-600 hover:text-amber-700 transition-colors"
        >
          {selectedProject?.icon} {selectedProject?.name}
        </button>
      );
    }

    if (currentView === 'room') {
      crumbs.push(<ChevronRight key="sep4" className="text-stone-400" size={16} />);
      crumbs.push(
        <span key="room" className="text-stone-700">
          💬 {selectedRoom?.name}
        </span>
      );
    }

    return <div className="flex items-center gap-2 text-sm">{crumbs}</div>;
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-black via-[#1E2A38] to-black">
      {/* Layered Background Images from Landing Page */}
      <div className="fixed inset-0" style={{ zIndex: 0 }}>
        {/* Grant Spiral - Bottom */}
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div 
            style={{
              transform: `rotate(${orbPhase * 0.3}deg)`,
              filter: 'blur(40px) brightness(1.2)',
              opacity: 0.15,
              width: '800px',
              height: '800px'
            }}
          >
            <GrantitudeSpiral size={800} />
          </div>
        </div>

        {/* Fractal Layer */}
        <div className="absolute inset-0 opacity-5">
          <img src="/spiral-fractals.jpeg" alt="" className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.6)' }} />
        </div>

        {/* Spiral World Layer */}
        <div className="absolute inset-0 opacity-5">
          <img src="/spiral-world.jpeg" alt="" className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.6)' }} />
        </div>

        {/* Owl Guardian - Subtle */}
        <div className="absolute inset-0 opacity-8">
          <img src="/owl-staring.jpeg" alt="" className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.5)' }} />
        </div>

        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen">
        {/* Header - Elegant Dark */}
        <div className="bg-black/80 backdrop-blur-xl border-b border-[#E6C685]/20 sticky top-0 z-20 shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={onNavigateBack}
                  className="text-[#CED3CC] hover:text-[#E6C685] transition-colors text-sm font-light flex items-center gap-1"
                  style={{ fontFamily: "'Cinzel', serif" }}
                >
                  ← Back
                </button>
                <div className="h-6 w-px bg-[#E6C685]/20"></div>
                <div className="flex items-center gap-2.5">
                  <Sparkles className="text-[#E6C685]" size={20} />
                  <h1 className="text-lg font-light text-[#E6C685] tracking-wider"
                    style={{ fontFamily: "'Cinzel', serif" }}>
                    spiral organizer
                  </h1>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-[#CED3CC]" title="Search">
                  <Search size={18} />
                </button>
                <button className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-[#CED3CC] relative" title="Notifications">
                  <Bell size={18} />
                  <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-[#E6C685] rounded-full"></span>
                </button>
                <button className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-[#CED3CC]" title="Profile">
                  <User size={18} />
                </button>
              </div>
            </div>

            {/* Breadcrumbs - Elegant & Minimal */}
            <div className="mt-2.5 pl-12">
              {renderBreadcrumbs()}
            </div>
          </div>
        </div>

        {/* Main View Area */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          {currentView === 'planet' && (
            <PlanetView 
              planetData={planetData}
              onHamletClick={handleHamletClick}
              onShowCreateHamlet={() => setShowCreateHamlet(true)}
            />
          )}

          {currentView === 'hamlet' && (
            <HamletView 
              hamlet={selectedHamlet}
              onFamilyClick={handleFamilyClick}
              onShowCreateFamily={() => setShowCreateFamily(true)}
              onBack={() => setCurrentView('planet')}
            />
          )}

          {currentView === 'family' && (
            <FamilyView 
              family={selectedFamily}
              onProjectClick={handleProjectClick}
              onShowCreateProject={() => setShowCreateProject(true)}
              onBack={() => setCurrentView('hamlet')}
            />
          )}

          {currentView === 'project' && (
            <ProjectView 
              project={selectedProject}
              onRoomClick={handleRoomClick}
              onShowCreateRoom={() => setShowCreateRoom(true)}
              onShowSixPerspectives={() => setShowSixPerspectives(true)}
              onBack={() => setCurrentView('family')}
            />
          )}

          {currentView === 'room' && (
            <RoomView 
              room={selectedRoom}
              messages={messages}
              tasks={tasks}
              newMessage={newMessage}
              setNewMessage={setNewMessage}
              onSendMessage={handleSendMessage}
              onShowRemedyCouncil={() => setShowRemedyCouncil(true)}
              onShowLoreScrapbook={() => setShowLoreScrapbook(true)}
              onShowEpicLoreLibrary={() => setShowEpicLoreLibrary(true)}
              onShowCreateTask={() => setShowCreateTask(true)}
              onToggleTask={handleToggleTask}
              onDeleteTask={handleDeleteTask}
              onBack={() => setCurrentView('project')}
              mairyStreaming={mairyStreaming}
              mairyStreamContent={mairyStreamContent}
              mairyThinking={mairyThinking}
            />
          )}
        </div>
      </div>

      {/* Six Perspectives Portal - Full Screen Sacred Experience */}
      {showSixPerspectives && (
        <SixPerspectivesPortal 
          projectName={selectedProject?.name || 'Your Project'}
          onClose={() => setShowSixPerspectives(false)}
        />
      )}

      {/* Creation Modals */}
      {showCreateHamlet && (
        <CreateHamletModal 
          onClose={() => setShowCreateHamlet(false)}
          onCreate={handleCreateHamlet}
        />
      )}

      {showCreateFamily && (
        <CreateFamilyModal 
          onClose={() => setShowCreateFamily(false)}
          onCreate={handleCreateFamily}
        />
      )}

      {showCreateProject && (
        <CreateProjectModal 
          onClose={() => setShowCreateProject(false)}
          onCreate={handleCreateProject}
        />
      )}

      {showCreateRoom && (
        <CreateRoomModal 
          onClose={() => setShowCreateRoom(false)}
          onCreate={handleCreateRoom}
        />
      )}

      {showCreateTask && (
        <CreateTaskModal 
          onClose={() => setShowCreateTask(false)}
          onCreate={handleCreateTask}
        />
      )}

      {/* Epic Lore Library - Full Screen Experience */}
      {showEpicLoreLibrary && (
        <EpicLoreLibrary 
          projectId={selectedProject?.id}
          projectName={selectedProject?.name || 'Your Project'}
          onClose={() => setShowEpicLoreLibrary(false)} 
        />
      )}

      {/* Icon Upload Modal */}
      {showIconUpload && (
        <IconUploadModal 
          onClose={() => setShowIconUpload(false)} 
          target={iconUploadTarget}
        />
      )}

      {/* Remedy Council Modal */}
      {showRemedyCouncil && (
        <RemedyCouncilModal onClose={() => setShowRemedyCouncil(false)} />
      )}

      {/* Lore Scrapbook Modal */}
      {showLoreScrapbook && (
        <LoreScrapbookModal onClose={() => setShowLoreScrapbook(false)} />
      )}
    </div>
  );
};

// Planet View Component
const PlanetView = ({ planetData, onHamletClick, onShowCreateHamlet }) => {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-4xl font-bold text-stone-800 mb-2">
          🌍 {planetData.name}
        </h2>
        <p className="text-stone-600 text-lg">
          "The Guild to Build Belonging"
        </p>
        <p className="text-amber-700 italic mt-2">
          Forgive, Forge, and Flow
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {planetData.hamlets.map(hamlet => (
          <button
            key={hamlet.id}
            onClick={() => onHamletClick(hamlet)}
            className="bg-gradient-to-br from-purple-100/80 to-stone-200/80 backdrop-blur-md border border-stone-300/50 rounded-2xl p-6 hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-300/40 text-left group"
          >
            <div className="text-6xl mb-4 group-hover:scale-110 transition-transform">
              {hamlet.icon}
            </div>
            <h3 className="text-2xl font-bold text-stone-800 mb-2">
              {hamlet.name}
            </h3>
            <p className="text-stone-600">
              {hamlet.families.length} {hamlet.families.length === 1 ? 'Family' : 'Families'}
            </p>
            <div className="mt-4 flex items-center gap-2 text-amber-700">
              <span className="text-sm">Enter Hamlet</span>
              <ChevronRight size={16} />
            </div>
          </button>
        ))}

        {/* Create New Hamlet */}
        <button 
          onClick={onShowCreateHamlet}
          className="bg-stone-100/80 backdrop-blur-md border-2 border-dashed border-stone-300/50 rounded-2xl p-6 hover:bg-stone-200/80 transition-all duration-300 flex flex-col items-center justify-center gap-3 group"
        >
          <Plus size={48} className="text-stone-400 group-hover:text-stone-600 transition-colors" />
          <span className="text-stone-600 group-hover:text-stone-800 transition-colors font-medium">
            Create New Hamlet
          </span>
        </button>
      </div>
    </div>
  );
};

// Hamlet View Component
const HamletView = ({ hamlet, onFamilyClick, onShowCreateFamily, onBack }) => {
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="text-6xl mb-4">{hamlet.icon}</div>
        <h2 className="text-4xl font-bold text-stone-800 mb-2">
          {hamlet.name}
        </h2>
        <p className="text-stone-600">
          A cluster of families and enterprises
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {hamlet.families.map(family => (
          <button
            key={family.id}
            onClick={() => onFamilyClick(family)}
            className="bg-gradient-to-br from-amber-100/80 to-orange-100/80 backdrop-blur-md border border-stone-300/50 rounded-2xl p-6 hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-amber-300/40 text-left group"
          >
            <div className="text-5xl mb-4 group-hover:scale-110 transition-transform">
              {family.icon}
            </div>
            <h3 className="text-2xl font-bold text-stone-800 mb-2">
              {family.name}
            </h3>
            <div className="flex items-center gap-4 text-sm text-stone-600 mb-4">
              <span className="flex items-center gap-1">
                <Users size={16} />
                {family.members} members
              </span>
              <span className="flex items-center gap-1">
                <FolderKanban size={16} />
                {family.projects.length} projects
              </span>
            </div>
            <div className="mt-4 flex items-center gap-2 text-amber-700">
              <span className="text-sm">Enter Family Space</span>
              <ChevronRight size={16} />
            </div>
          </button>
        ))}

        {/* Create New Family */}
        <button 
          onClick={onShowCreateFamily}
          className="bg-stone-100/80 backdrop-blur-md border-2 border-dashed border-stone-300/50 rounded-2xl p-6 hover:bg-stone-200/80 transition-all duration-300 flex flex-col items-center justify-center gap-3 group"
        >
          <Plus size={48} className="text-stone-400 group-hover:text-stone-600 transition-colors" />
          <span className="text-stone-600 group-hover:text-stone-800 transition-colors font-medium">
            Create New Family
          </span>
        </button>
      </div>
    </div>
  );
};

// Family View Component
const FamilyView = ({ family, onProjectClick, onShowCreateProject, onBack }) => {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-amber-100/70 to-orange-100/70 backdrop-blur-md border border-stone-300/50 rounded-2xl p-8 mb-8">
        <div className="flex items-start gap-6">
          <div className="text-6xl">{family.icon}</div>
          <div className="flex-1">
            <h2 className="text-4xl font-bold text-stone-800 mb-2">
              {family.name}
            </h2>
            <p className="text-stone-600 mb-4">
              The hearth fire where belonging begins
            </p>
            <div className="flex items-center gap-4 text-stone-700">
              <span className="flex items-center gap-2">
                <Users size={20} />
                {family.members} members
              </span>
              <span className="flex items-center gap-2">
                <FolderKanban size={20} />
                {family.projects.length} active projects
              </span>
            </div>
          </div>
        </div>
      </div>

      <h3 className="text-2xl font-bold text-stone-800 mb-4">Active Projects</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {family.projects.map(project => (
          <button
            key={project.id}
            onClick={() => onProjectClick(project)}
            className="bg-gradient-to-br from-purple-100/80 to-lavender-100/80 backdrop-blur-md border border-stone-300/50 rounded-2xl p-6 hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-300/40 text-left group"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="text-4xl group-hover:scale-110 transition-transform">
                {project.icon}
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                project.status === 'active' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-stone-200 text-stone-600'
              }`}>
                {project.status}
              </div>
            </div>
            
            <h3 className="text-xl font-bold text-stone-800 mb-3">
              {project.name}
            </h3>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm text-stone-600 mb-1">
                <span>Progress</span>
                <span>{project.progress}%</span>
              </div>
              <div className="w-full h-2 bg-stone-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-400 to-lavender-400 transition-all duration-500"
                  style={{ width: `${project.progress}%` }}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-600">
                {project.rooms.length} rooms
              </span>
              <div className="flex items-center gap-2 text-[#E6C685]">
                <span className="text-sm">Open Project</span>
                <ChevronRight size={16} />
              </div>
            </div>
          </button>
        ))}

        {/* Create New Project */}
        <button 
          onClick={onShowCreateProject}
          className="bg-stone-100/80 backdrop-blur-md border-2 border-dashed border-stone-300/50 rounded-2xl p-6 hover:bg-stone-200/80 transition-all duration-300 flex flex-col items-center justify-center gap-3 group"
        >
          <Plus size={48} className="text-stone-400 group-hover:text-stone-600 transition-colors" />
          <span className="text-stone-600 group-hover:text-stone-800 transition-colors font-medium">
            Create New Project
          </span>
        </button>
      </div>
    </div>
  );
};

// Project View Component
const ProjectView = ({ project, onRoomClick, onShowCreateRoom, onShowSixPerspectives, onBack }) => {
  const [activeTab, setActiveTab] = React.useState('overview'); // overview, calendar, voting
  
  // Calculate project health
  const calculateHealth = () => {
    const factors = {
      progress: project.progress || 0,
      activity: 85, // Mock: would track recent messages/tasks
      consensus: 90, // Mock: would track voting agreement
      engagement: 78 // Mock: would track member participation
    };
    const overall = (factors.progress + factors.activity + factors.consensus + factors.engagement) / 4;
    return { ...factors, overall };
  };
  
  const health = calculateHealth();
  const healthColor = health.overall >= 80 ? '#10B981' : health.overall >= 60 ? '#F59E0B' : '#EF4444';
  const healthStatus = health.overall >= 80 ? 'Healthy' : health.overall >= 60 ? 'Needs Attention' : 'Critical';

  return (
    <div className="space-y-6">
      {/* Enhanced Header with Health Indicator */}
      <div className="bg-gradient-to-br from-black/60 to-[#1E2A38]/60 backdrop-blur-md border border-[#E6C685]/20 rounded-2xl p-8 mb-8">
        <div className="flex items-start gap-6">
          <div className="text-6xl">{project.icon}</div>
          <div className="flex-1">
            <div className="flex items-start justify-between mb-2">
              <h2 className="text-4xl font-light text-[#E6C685] mb-2" style={{ fontFamily: "'Cinzel', serif" }}>
                {project.name}
              </h2>
              {/* Health Badge */}
              <div className="flex items-center gap-2 px-4 py-2 rounded-full border"
                style={{ borderColor: healthColor, backgroundColor: `${healthColor}20` }}>
                <div className="w-3 h-3 rounded-full animate-pulse" style={{ backgroundColor: healthColor }}></div>
                <span className="text-sm font-light text-white">{healthStatus}</span>
              </div>
            </div>
            
            <p className="text-[#CED3CC] mb-4 font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Where action meets belonging
            </p>
            
            {/* Progress */}
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm text-[#CED3CC] mb-2">
                <span>Overall Progress</span>
                <span className="font-bold text-[#E6C685]">{project.progress}%</span>
              </div>
              <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#5C2D73] to-[#E6C685] transition-all duration-500"
                  style={{ width: `${project.progress}%` }}
                />
              </div>
            </div>

            {/* Quick Stats */}
            <div className="flex items-center gap-6 text-[#CED3CC]">
              <span className="flex items-center gap-2">
                <MessageSquare size={18} className="text-[#E6C685]" />
                {project.rooms.filter(r => r.type === 'chat').length} chat rooms
              </span>
              <span className="flex items-center gap-2">
                <CheckSquare size={18} className="text-[#E6C685]" />
                {project.rooms.filter(r => r.type === 'tasks').length} task rooms
              </span>
              <span className="flex items-center gap-2">
                <Heart size={18} className="text-[#E6C685]" />
                {Math.round(health.engagement)}% engaged
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-2 mb-6 border-b border-[#E6C685]/20 pb-4">
        {[
          { id: 'overview', label: 'Overview', icon: FolderKanban },
          { id: 'calendar', label: 'Calendar', icon: Calendar },
          { id: 'voting', label: 'Consensus', icon: Shield }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === tab.id
                ? 'bg-[#E6C685]/20 text-[#E6C685] border border-[#E6C685]/40'
                : 'text-[#CED3CC] hover:bg-white/5'
            }`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-light text-[#E6C685]" style={{ fontFamily: "'Cinzel', serif" }}>
                📋 Rooms (Salons)
              </h3>
              <p className="text-[#CED3CC] font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Each room is a threaded space for chat, creation, and decision-making
              </p>
            </div>
            
            <button
              onClick={onShowSixPerspectives}
              className="px-6 py-3 bg-gradient-to-r from-[#5C2D73] to-[#E6C685] hover:opacity-90 text-white rounded-xl font-light transition-all shadow-lg hover:shadow-xl flex items-center gap-2 group"
              style={{ fontFamily: "'Cinzel', serif" }}
            >
              <Eye size={20} className="group-hover:scale-110 transition-transform" />
              Six Perspectives
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {project.rooms.map(room => (
          <button
            key={room.id}
            onClick={() => onRoomClick(room)}
            className="bg-gradient-to-br from-stone-100/90 to-purple-100/90 backdrop-blur-md border border-stone-300/50 rounded-xl p-5 hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-purple-300/40 text-left group relative"
          >
            {room.unread > 0 && (
              <div className="absolute top-3 right-3 bg-amber-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                {room.unread}
              </div>
            )}
            
            <div className="text-3xl mb-3">
              {room.type === 'chat' ? '💬' : '✅'}
            </div>
            
            <h4 className="text-lg font-bold text-stone-800 mb-2">
              {room.name}
            </h4>
            
            <p className="text-sm text-stone-600 mb-3">
              {room.type === 'chat' ? 'Chat & Discussion' : 'Tasks & Actions'}
            </p>

            <div className="flex items-center gap-2 text-[#E6C685]">
              <span className="text-sm">Enter Room</span>
              <ChevronRight size={14} />
            </div>
          </button>
        ))}

        {/* Create New Room */}
        <button 
          onClick={onShowCreateRoom}
          className="bg-white/5 backdrop-blur-md border-2 border-dashed border-[#E6C685]/30 rounded-xl p-5 hover:bg-white/10 transition-all duration-300 flex flex-col items-center justify-center gap-3 group"
        >
          <Plus size={36} className="text-[#E6C685] group-hover:scale-110 transition-transform" />
          <span className="text-[#CED3CC] group-hover:text-[#E6C685] transition-colors text-sm font-light"
            style={{ fontFamily: "'Cinzel', serif" }}>
            Create New Room
          </span>
        </button>
      </div>
        </>
      )}

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <div className="bg-black/60 backdrop-blur-md border border-[#E6C685]/20 rounded-2xl p-8">
          <h3 className="text-2xl font-light text-[#E6C685] mb-6" style={{ fontFamily: "'Cinzel', serif" }}>
            📅 Project Calendar
          </h3>
          
          {/* Simple Calendar View */}
          <div className="grid grid-cols-7 gap-2 mb-6">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-[#E6C685] text-sm font-light py-2">
                {day}
              </div>
            ))}
            {[...Array(35)].map((_, i) => {
              const dayNum = i - 2;
              const hasEvent = [5, 12, 18, 25].includes(dayNum);
              return (
                <div
                  key={i}
                  className={`aspect-square flex items-center justify-center rounded-lg text-sm transition-all
                    ${dayNum < 1 || dayNum > 30 ? 'text-[#CED3CC]/30' : 'text-[#CED3CC] hover:bg-white/10 cursor-pointer'}
                    ${hasEvent ? 'bg-[#E6C685]/20 border border-[#E6C685]/40 font-bold' : 'bg-white/5'}
                  `}
                >
                  {dayNum > 0 && dayNum <= 30 ? dayNum : ''}
                </div>
              );
            })}
          </div>

          {/* Upcoming Events */}
          <div className="space-y-3">
            <h4 className="text-lg font-light text-[#E6C685] mb-4">Upcoming Events</h4>
            {[
              { date: 'Oct 15', title: 'Team Sync', time: '2:00 PM', attendees: 8 },
              { date: 'Oct 18', title: 'Design Review', time: '10:00 AM', attendees: 5 },
              { date: 'Oct 22', title: 'Launch Planning', time: '3:30 PM', attendees: 12 }
            ].map((event, i) => (
              <div key={i} className="flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 rounded-lg transition-all cursor-pointer">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-[#E6C685] text-sm font-bold">{event.date.split(' ')[1]}</div>
                    <div className="text-[#CED3CC]/60 text-xs">{event.date.split(' ')[0]}</div>
                  </div>
                  <div>
                    <div className="text-[#CED3CC] font-light">{event.title}</div>
                    <div className="text-[#CED3CC]/60 text-sm">{event.time}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-[#E6C685] text-sm">
                  <Users size={14} />
                  {event.attendees}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Voting/Consensus Tab */}
      {activeTab === 'voting' && (
        <div className="space-y-6">
          <div className="bg-black/60 backdrop-blur-md border border-[#E6C685]/20 rounded-2xl p-8">
            <h3 className="text-2xl font-light text-[#E6C685] mb-6" style={{ fontFamily: "'Cinzel', serif" }}>
              🗳️ Consensus Voting
            </h3>
            
            {/* Active Proposals */}
            <div className="space-y-4">
              {[
                { 
                  id: 1, 
                  title: 'Approve Q4 Budget Increase', 
                  status: 'active', 
                  votes: { yes: 12, no: 2, abstain: 1 },
                  deadline: '2 days left'
                },
                { 
                  id: 2, 
                  title: 'Change Meeting Time to Fridays', 
                  status: 'active', 
                  votes: { yes: 8, no: 5, abstain: 2 },
                  deadline: '5 days left'
                },
                { 
                  id: 3, 
                  title: 'Hire Additional Designer', 
                  status: 'passed', 
                  votes: { yes: 14, no: 1, abstain: 0 },
                  deadline: 'Completed'
                }
              ].map(proposal => {
                const total = proposal.votes.yes + proposal.votes.no + proposal.votes.abstain;
                const yesPercent = (proposal.votes.yes / total * 100).toFixed(0);
                const consensus = yesPercent >= 80 ? 'high' : yesPercent >= 60 ? 'medium' : 'low';
                const consensusColor = consensus === 'high' ? '#10B981' : consensus === 'medium' ? '#F59E0B' : '#EF4444';
                
                return (
                  <div key={proposal.id} className="bg-white/5 border border-[#E6C685]/20 rounded-xl p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h4 className="text-lg font-light text-[#E6C685] mb-2">{proposal.title}</h4>
                        <div className="flex items-center gap-4 text-sm text-[#CED3CC]/80">
                          <span className="flex items-center gap-2">
                            <Clock size={14} />
                            {proposal.deadline}
                          </span>
                          <span className="flex items-center gap-2">
                            <Users size={14} />
                            {total} votes
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1 rounded-full text-xs"
                        style={{ backgroundColor: `${consensusColor}20`, color: consensusColor, border: `1px solid ${consensusColor}` }}>
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: consensusColor }}></div>
                        {yesPercent}% consensus
                      </div>
                    </div>

                    {/* Vote Breakdown */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden flex">
                          <div className="bg-green-500 h-full" style={{ width: `${proposal.votes.yes / total * 100}%` }}></div>
                          <div className="bg-red-500 h-full" style={{ width: `${proposal.votes.no / total * 100}%` }}></div>
                          <div className="bg-gray-500 h-full" style={{ width: `${proposal.votes.abstain / total * 100}%` }}></div>
                        </div>
                      </div>
                      <div className="flex items-center gap-6 text-xs text-[#CED3CC]/80">
                        <span className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                          Yes ({proposal.votes.yes})
                        </span>
                        <span className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          No ({proposal.votes.no})
                        </span>
                        <span className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                          Abstain ({proposal.votes.abstain})
                        </span>
                      </div>
                    </div>

                    {/* Voting Buttons */}
                    {proposal.status === 'active' && (
                      <div className="flex items-center gap-2">
                        <button className="flex-1 px-4 py-2 bg-green-500/20 border border-green-500/40 text-green-400 rounded-lg hover:bg-green-500/30 transition-all text-sm">
                          Vote Yes
                        </button>
                        <button className="flex-1 px-4 py-2 bg-red-500/20 border border-red-500/40 text-red-400 rounded-lg hover:bg-red-500/30 transition-all text-sm">
                          Vote No
                        </button>
                        <button className="flex-1 px-4 py-2 bg-gray-500/20 border border-gray-500/40 text-gray-400 rounded-lg hover:bg-gray-500/30 transition-all text-sm">
                          Abstain
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Room View Component (Main Collaboration Space)
const RoomView = ({ 
  room, 
  messages, 
  tasks, 
  newMessage, 
  setNewMessage, 
  onSendMessage,
  onShowRemedyCouncil,
  onShowLoreScrapbook,
  onShowEpicLoreLibrary,
  onShowCreateTask,
  onToggleTask,
  onDeleteTask,
  onBack,
  mairyStreaming,
  mairyStreamContent,
  mairyThinking
}) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 min-h-[calc(100vh-200px)]">
      {/* Main Chat/Messaging Area */}
      <div className="lg:col-span-2 bg-gradient-to-br from-stone-100/90 to-purple-100/90 backdrop-blur-md border border-stone-300/50 rounded-2xl overflow-hidden flex flex-col">
        {/* Room Header */}
        <div className="bg-stone-700/90 border-b border-stone-600/50 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-stone-100 flex items-center gap-2">
                💬 {room.name}
              </h3>
              <p className="text-sm text-stone-300 mt-1">
                3 members present
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <button className="p-2 rounded-lg bg-green-600/30 hover:bg-green-600/40 transition-colors text-green-200" title="Start Video Call">
                <Video size={20} />
              </button>
              <button className="p-2 rounded-lg bg-gradient-to-r from-[#5C2D73] to-[#E6C685]/30 hover:bg-gradient-to-r from-[#5C2D73] to-[#E6C685]/40 transition-colors text-purple-200" title="Start Voice Call">
                <Phone size={20} />
              </button>
              <button 
                onClick={onShowEpicLoreLibrary}
                className="p-2 rounded-lg bg-amber-600/30 hover:bg-amber-600/40 transition-colors text-amber-200" 
                title="Open Epic Lore Library"
              >
                <BookOpen size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-stone-50/50">
          {messages.map(message => (
            <div 
              key={message.id}
              className={`flex gap-3 ${message.sender === 'You' ? 'flex-row-reverse' : 'flex-row'}`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                message.isAI 
                  ? 'bg-gradient-to-br from-purple-400 to-lavender-400'
                  : message.sender === 'You'
                  ? 'bg-gradient-to-br from-amber-400 to-orange-400'
                  : 'bg-gradient-to-br from-stone-400 to-stone-500'
              }`}>
                {message.isAI ? '✨' : message.sender[0]}
              </div>
              
              <div className={`flex-1 max-w-md ${message.sender === 'You' ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                <div className="flex items-center gap-2 text-xs text-stone-600">
                  <span className="font-medium">{message.sender}</span>
                  <span>{message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  {message.streaming && <span className="text-purple-600">• Live AI</span>}
                </div>
                
                <div className={`px-4 py-2 rounded-2xl ${
                  message.isAI 
                    ? 'bg-gradient-to-br from-purple-100 to-lavender-100 border border-[#E6C685]/30/50'
                    : message.sender === 'You'
                    ? 'bg-gradient-to-br from-amber-100 to-orange-100'
                    : 'bg-white/80'
                }`}>
                  <p className="text-stone-800 text-sm whitespace-pre-wrap">{message.text}</p>
                </div>

                {message.hasTask && !message.isAI && (
                  <button className="text-xs text-amber-700 hover:text-amber-800 flex items-center gap-1 mt-1">
                    <Sparkles size={12} />
                    Create task from this
                  </button>
                )}
              </div>
            </div>
          ))}
          
          {/* Streaming M.ai.ry message (in progress) */}
          {mairyStreaming && mairyStreamContent && (
            <div className="flex gap-3 flex-row">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm bg-gradient-to-br from-purple-400 to-lavender-400 animate-pulse">
                ✨
              </div>
              
              <div className="flex-1 max-w-md flex flex-col gap-1">
                <div className="flex items-center gap-2 text-xs text-stone-600">
                  <span className="font-medium">M.ai.ry</span>
                  <span className="text-purple-600 animate-pulse">• streaming...</span>
                </div>
                
                <div className="px-4 py-2 rounded-2xl bg-gradient-to-br from-purple-100 to-lavender-100 border border-purple-300/50">
                  <p className="text-stone-800 text-sm whitespace-pre-wrap">{mairyStreamContent}</p>
                  <div className="flex items-center gap-1 mt-2 text-xs text-purple-600">
                    <Sparkles size={10} className="animate-spin" />
                    <span>generating response...</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* M.ai.ry thinking indicator */}
          {mairyThinking && !mairyStreamContent && (
            <div className="flex gap-3 flex-row">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm bg-gradient-to-br from-purple-400 to-lavender-400 animate-pulse">
                ✨
              </div>
              
              <div className="flex-1 max-w-md">
                <div className="px-4 py-2 rounded-2xl bg-gradient-to-br from-purple-100 to-lavender-100">
                  <div className="flex items-center gap-2 text-sm text-purple-600">
                    <Sparkles size={14} className="animate-spin" />
                    <span>M.ai.ry is contemplating...</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Message Input */}
        <div className="border-t border-stone-300/50 p-4 bg-white/60">
          <div className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && onSendMessage()}
              placeholder="Type a message... M.ai.ry listens for tasks"
              className="flex-1 bg-white border border-stone-300 rounded-xl px-4 py-3 text-stone-800 placeholder-stone-400 focus:outline-none focus:border-purple-400"
            />
            <button
              onClick={onSendMessage}
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-lavender-500 hover:from-purple-600 hover:to-lavender-600 rounded-xl text-white font-medium transition-all flex items-center gap-2"
            >
              <Send size={18} />
            </button>
          </div>
          
          <div className="mt-2 text-xs text-stone-500 flex items-center gap-4">
            <span>✨ M.ai.ry auto-detects tasks and creates gentle reminders</span>
            <button 
              onClick={onShowRemedyCouncil}
              className="text-rose-600 hover:text-rose-700 flex items-center gap-1"
            >
              <Shield size={12} />
              Need a Remedy Council?
            </button>
          </div>
        </div>
      </div>

      {/* Tasks & Context Sidebar */}
      <div className="space-y-4">
        {/* Active Tasks */}
        <div className="bg-gradient-to-br from-purple-100/80 to-lavender-100/80 backdrop-blur-md border border-stone-300/50 rounded-2xl p-5">
          <h4 className="text-lg font-bold text-stone-800 mb-4 flex items-center gap-2">
            <CheckSquare size={20} />
            Active Tasks
          </h4>
          
          <div className="space-y-1.5">
            {tasks.map(task => (
              <div 
                key={task.id}
                className="bg-white/90 border border-stone-200 rounded-lg p-2 hover:bg-white hover:shadow-sm transition-all group"
              >
                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={task.status === 'completed'}
                    onChange={() => onToggleTask(task.id)}
                    className="w-3.5 h-3.5 rounded border-stone-300 flex-shrink-0 cursor-pointer"
                  />
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-medium truncate ${
                      task.status === 'completed' 
                        ? 'text-stone-400 line-through' 
                        : 'text-stone-800'
                    }`}>
                      {task.text}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
                      task.priority === 'high' 
                        ? 'bg-red-100 text-red-700'
                        : task.priority === 'medium'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {task.priority}
                    </span>
                    <span className="text-stone-500 text-[9px] flex items-center gap-0.5">
                      <Clock size={10} />
                      {task.dueDate}
                    </span>
                    <button
                      onClick={() => onDeleteTask(task.id)}
                      className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-red-100 rounded transition-all"
                      title="Delete task"
                    >
                      <X size={12} className="text-red-500" />
                    </button>
                  </div>
                </div>
                <p className="text-[10px] text-stone-500 mt-1 ml-5 truncate">
                  {task.context}
                </p>
              </div>
            ))}
          </div>

          <button 
            onClick={onShowCreateTask}
            className="w-full mt-4 py-2 bg-stone-100 hover:bg-stone-200 border border-stone-300 rounded-xl text-stone-700 text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            <Plus size={16} />
            Add Task
          </button>
        </div>

        {/* Quick Actions */}
        <div className="bg-gradient-to-br from-amber-100/80 to-orange-100/80 backdrop-blur-md border border-stone-300/50 rounded-2xl p-5">
          <h4 className="text-lg font-bold text-stone-800 mb-4">Quick Actions</h4>
          
          <div className="space-y-2">
            <button 
              onClick={onShowRemedyCouncil}
              className="w-full py-2 bg-rose-100 hover:bg-rose-200 border border-rose-300 rounded-xl text-rose-700 text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <Shield size={16} />
              Convene Remedy Council
            </button>
            
            <button className="w-full py-2 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded-xl text-amber-700 text-sm font-medium transition-colors flex items-center justify-center gap-2">
              <Calendar size={16} />
              Schedule Gathering
            </button>
            
            <button 
              onClick={onShowEpicLoreLibrary}
              className="w-full py-2 bg-[#E6C685]/10 hover:bg-[#E6C685]/20 border border-[#E6C685]/30 rounded-xl text-[#5C2D73] text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <BookOpen size={16} />
              Epic Lore Library
            </button>
          </div>
        </div>

        {/* M.ai.ry Insight */}
        <div className="bg-gradient-to-br from-lavender-100/80 to-purple-100/80 backdrop-blur-md border border-[#E6C685]/30/50 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <Sparkles className="text-[#E6C685] mt-1" size={20} />
            <div>
              <h4 className="text-sm font-bold text-stone-800 mb-2">M.ai.ry's Insight</h4>
              <p className="text-xs text-stone-700 leading-relaxed">
                "The energy in this room feels productive. I've detected 2 actionable items and created gentle reminders. Would anyone like me to summarize the conversation so far?"
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Remedy Council Modal
const RemedyCouncilModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-rose-900/90 to-red-900/90 backdrop-blur-xl border-2 border-rose-500/50 rounded-3xl max-w-2xl w-full p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
        >
          <X size={24} />
        </button>

        <div className="text-center mb-6">
          <Shield className="text-rose-300 mx-auto mb-4" size={48} />
          <h2 className="text-3xl font-bold text-white mb-2">
            Remedy Council
          </h2>
          <p className="text-rose-200 italic">
            "Forgive, Forge, and Flow"
          </p>
        </div>

        <div className="space-y-4 text-white/90">
          <p className="leading-relaxed">
            The Remedy Council is a sacred space to transmute friction into fuel. 
            When tension arises, we gather to breathe, speak truth, forgive, and forge ahead together.
          </p>

          <div className="bg-black/30 rounded-xl p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-rose-500 flex items-center justify-center text-sm font-bold">1</div>
              <div>
                <h4 className="font-bold mb-1">Council 1 - Direct Remedy</h4>
                <p className="text-sm text-white/70">Only those in conflict attend. Breathe → Speak truth → Forgive → Forge ahead.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-rose-500 flex items-center justify-center text-sm font-bold">2</div>
              <div>
                <h4 className="font-bold mb-1">Council 2 - Ripple Council</h4>
                <p className="text-sm text-white/70">Those affected indirectly can speak and clear emotional residue.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-rose-500 flex items-center justify-center text-sm font-bold">3</div>
              <div>
                <h4 className="font-bold mb-1">Close with Unity</h4>
                <p className="text-sm text-white/70">"We release the story. We remember the mission."</p>
              </div>
            </div>
          </div>

          <button className="w-full py-3 bg-gradient-to-r from-rose-500 to-red-500 hover:from-rose-600 hover:to-red-600 rounded-xl text-white font-bold transition-all">
            Initiate Remedy Council
          </button>
        </div>
      </div>
    </div>
  );
};

// Create Hamlet Modal
const CreateHamletModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({ name: '', icon: '🌟' });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name.trim()) {
      onCreate(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 className="text-xl font-semibold text-stone-800 mb-4">Create New Hamlet</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Hamlet Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., West Coast Hamlet"
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-purple-400"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Icon (emoji)</label>
            <input
              type="text"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              placeholder="🌟"
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-purple-400"
              maxLength={2}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 py-2 px-4 bg-[#E6C685] hover:bg-gradient-to-r from-[#5C2D73] to-[#E6C685] text-white rounded-lg transition-colors"
            >
              Create Hamlet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Family Modal
const CreateFamilyModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({ 
    name: '', 
    icon: '🏡', 
    members: '1', 
    type: 'family',
    iconImage: null 
  });
  const [imagePreview, setImagePreview] = useState(null);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData({ ...formData, iconImage: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name.trim()) {
      onCreate(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-semibold text-stone-800 mb-4">Create New Family or Enterprise</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type Selection */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Type</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'family', icon: '🏡' })}
                className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${
                  formData.type === 'family'
                    ? 'border-[#E6C685] bg-[#E6C685]/5 text-[#5C2D73]'
                    : 'border-stone-200 text-stone-600 hover:border-stone-300'
                }`}
              >
                🏡 Family
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'enterprise', icon: '🏢' })}
                className={`flex-1 py-2 px-4 rounded-lg border-2 transition-all ${
                  formData.type === 'enterprise'
                    ? 'border-[#E6C685] bg-[#E6C685]/5 text-[#5C2D73]'
                    : 'border-stone-200 text-stone-600 hover:border-stone-300'
                }`}
              >
                🏢 Enterprise
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={formData.type === 'family' ? 'e.g., Johnson Family' : 'e.g., Green Tech Solutions'}
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-purple-400"
              autoFocus
            />
          </div>

          {/* Icon Selection */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-2">Icon</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                placeholder="🏡"
                className="flex-1 px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-purple-400"
                maxLength={2}
              />
              <label className="px-4 py-2 bg-stone-100 hover:bg-stone-200 border border-stone-300 rounded-lg cursor-pointer transition-colors flex items-center gap-2">
                <ImageIcon size={16} />
                Upload
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            </div>
            {imagePreview && (
              <img src={imagePreview} alt="Preview" className="mt-2 w-16 h-16 rounded-lg object-cover" />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Number of Members</label>
            <input
              type="number"
              value={formData.members}
              onChange={(e) => setFormData({ ...formData, members: e.target.value })}
              min="1"
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-purple-400"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 py-2 px-4 bg-[#E6C685] hover:bg-gradient-to-r from-[#5C2D73] to-[#E6C685] text-white rounded-lg transition-colors"
            >
              Create {formData.type === 'family' ? 'Family' : 'Enterprise'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Project Modal
const CreateProjectModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({ name: '', icon: '📋', description: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name.trim()) {
      onCreate(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 className="text-xl font-semibold text-stone-800 mb-4">Create New Project</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Project Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Summer Vacation Planning"
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-purple-400"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Icon (emoji)</label>
            <input
              type="text"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              placeholder="📋"
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-purple-400"
              maxLength={2}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Description (optional)</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="What's this project about?"
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-purple-400"
              rows="3"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 py-2 px-4 bg-[#E6C685] hover:bg-gradient-to-r from-[#5C2D73] to-[#E6C685] text-white rounded-lg transition-colors"
            >
              Create Project
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Room Modal
const CreateRoomModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({ name: '', type: 'chat' });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name.trim()) {
      onCreate(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 className="text-xl font-semibold text-stone-800 mb-4">Create New Room</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Room Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Planning Salon"
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-purple-400"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Room Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-purple-400"
            >
              <option value="chat">Chat & Discussion</option>
              <option value="tasks">Tasks & Actions</option>
            </select>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 py-2 px-4 bg-[#E6C685] hover:bg-gradient-to-r from-[#5C2D73] to-[#E6C685] text-white rounded-lg transition-colors"
            >
              Create Room
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Create Task Modal
const CreateTaskModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({ 
    text: '', 
    assignedTo: '', 
    dueDate: '', 
    priority: 'medium' 
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.text.trim()) {
      onCreate(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 className="text-xl font-semibold text-stone-800 mb-4">Create New Task</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Task Description</label>
            <input
              type="text"
              value={formData.text}
              onChange={(e) => setFormData({ ...formData, text: e.target.value })}
              placeholder="e.g., Call venue to confirm booking"
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-purple-400"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Assigned To</label>
            <input
              type="text"
              value={formData.assignedTo}
              onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
              placeholder="e.g., Craig"
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-purple-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Due Date</label>
            <input
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-purple-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-purple-400"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 py-2 px-4 bg-[#E6C685] hover:bg-gradient-to-r from-[#5C2D73] to-[#E6C685] text-white rounded-lg transition-colors"
            >
              Create Task
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Icon Upload Modal
const IconUploadModal = ({ onClose, target }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = () => {
    // In production, this would upload to backend
    console.log('Uploading icon:', selectedFile);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-stone-400 hover:text-stone-600 transition-colors"
        >
          <X size={20} />
        </button>

        <h3 className="text-xl font-semibold text-stone-800 mb-4">
          Upload Custom Icon
        </h3>
        
        <p className="text-sm text-stone-600 mb-4">
          Choose an image to use as your {target} icon. Recommended size: 256x256px
        </p>

        <div className="border-2 border-dashed border-stone-300 rounded-xl p-6 text-center mb-4">
          {previewUrl ? (
            <div className="space-y-3">
              <img src={previewUrl} alt="Preview" className="w-24 h-24 mx-auto rounded-xl object-cover" />
              <button
                onClick={() => {
                  setSelectedFile(null);
                  setPreviewUrl(null);
                }}
                className="text-sm text-stone-500 hover:text-stone-700"
              >
                Change Image
              </button>
            </div>
          ) : (
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              <div className="text-stone-400 mb-2">
                <Plus size={32} className="mx-auto" />
              </div>
              <p className="text-sm text-stone-600">Click to select an image</p>
            </label>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2 px-4 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!selectedFile}
            className="flex-1 py-2 px-4 bg-[#E6C685] hover:bg-gradient-to-r from-[#5C2D73] to-[#E6C685] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Upload Icon
          </button>
        </div>
      </div>
    </div>
  );
};

// Lore Scrapbook Modal
const LoreScrapbookModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-amber-100/95 to-orange-100/95 backdrop-blur-xl border-2 border-stone-300 rounded-3xl max-w-4xl w-full max-h-[80vh] overflow-y-auto p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
        >
          <X size={24} />
        </button>

        <div className="text-center mb-6">
          <BookOpen className="text-amber-600 mx-auto mb-4" size={48} />
          <h2 className="text-3xl font-bold text-stone-800 mb-2">
            Lore Scrapbook
          </h2>
          <p className="text-amber-700 italic">
            The living memory of your project
          </p>
        </div>

        <div className="space-y-6">
          {/* Timeline Entry */}
          <div className="bg-white/60 rounded-xl p-5 border-l-4 border-amber-500">
            <div className="flex items-start gap-4">
              <div className="text-3xl">📅</div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-bold text-stone-800">Project Kickoff</h4>
                  <span className="text-xs text-stone-500">3 days ago</span>
                </div>
                <p className="text-stone-700 text-sm mb-3">
                  The Thompson family gathered to begin planning the 2025 reunion. 
                  Initial goals set: venue selection, guest list, and budget planning.
                </p>
                <div className="flex gap-2">
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">3 tasks created</span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">5 participants</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/60 rounded-xl p-5 border-l-4 border-green-500">
            <div className="flex items-start gap-4">
              <div className="text-3xl">✅</div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-bold text-stone-800">First Milestone</h4>
                  <span className="text-xs text-stone-500">2 days ago</span>
                </div>
                <p className="text-stone-700 text-sm mb-3">
                  Venue secured! The family chose Riverside Gardens with capacity for 100 guests. 
                  Craig negotiated a great rate including catering options.
                </p>
                <div className="flex gap-2">
                  <span className="px-2 py-1 bg-[#E6C685]/10 text-[#5C2D73] text-xs rounded">Major decision</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/60 rounded-xl p-5 border-l-4 border-rose-500">
            <div className="flex items-start gap-4">
              <div className="text-3xl">⚕️</div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-bold text-stone-800">Remedy Council</h4>
                  <span className="text-xs text-stone-500">Yesterday</span>
                </div>
                <p className="text-stone-700 text-sm mb-3">
                  Brief tension arose about budget allocation. Council convened, 
                  all parties spoke, and agreement reached. Harmony restored.
                </p>
                <div className="flex gap-2">
                  <span className="px-2 py-1 bg-rose-100 text-rose-700 text-xs rounded">Conflict resolved</span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center pt-4">
            <p className="text-stone-600 text-sm italic">
              "M.ai.ry automatically chronicles your journey, preserving both triumphs and transformations."
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuildProjectManagement;
